/**
 * MIRA CurrencyInput Component
 * Wraps Input component with automatic currency formatting ($, commas)
 */

import React from 'react';
import { Input } from '@/components/mira';
import { cn } from '@/lib/utils';

/**
 * Format number to currency string with $ and commas
 * @param {string|number} value - Raw numeric value
 * @returns {string} - Formatted currency string (e.g., "$1,234.56")
 */
const formatCurrency = (value) => {
  if (value === '' || value === null || value === undefined) {
    return '';
  }

  // Remove non-numeric characters except decimal point
  const numericValue = String(value).replace(/[^0-9.]/g, '');

  if (numericValue === '') {
    return '';
  }

  const number = parseFloat(numericValue);

  if (isNaN(number)) {
    return '';
  }

  return `$${number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;
};

/**
 * Parse currency string to raw numeric value
 * @param {string} formatted - Formatted currency string
 * @returns {string} - Raw numeric string
 */
const parseCurrency = (formatted) => {
  if (!formatted) {
    return '';
  }

  // Remove $ and commas, keep only numbers and decimal point
  return formatted.replace(/[$,]/g, '');
};

export const CurrencyInput = React.forwardRef(
  ({
    value,
    onChange,
    onBlur,
    label,
    error,
    helperText,
    required,
    disabled,
    className,
    ...props
  }, ref) => {
    const [displayValue, setDisplayValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);

    // Update display value when prop value changes
    React.useEffect(() => {
      if (!isFocused) {
        setDisplayValue(formatCurrency(value));
      }
    }, [value, isFocused]);

    const handleFocus = (e) => {
      setIsFocused(true);
      // Show raw numeric value while editing
      setDisplayValue(parseCurrency(displayValue));
      props.onFocus?.(e);
    };

    const handleBlur = (e) => {
      setIsFocused(false);
      // Format on blur
      const numeric = parseCurrency(displayValue);
      setDisplayValue(formatCurrency(numeric));

      // Call parent onChange with raw numeric value
      if (onChange) {
        onChange(numeric);
      }

      if (onBlur) {
        onBlur(e);
      }
    };

    const handleChange = (e) => {
      const newValue = e.target.value;

      // Allow only numbers and one decimal point while typing
      if (newValue === '' || /^[0-9]*\.?[0-9]*$/.test(newValue)) {
        setDisplayValue(newValue);

        // Call parent onChange with raw numeric value (only when focused)
        if (onChange && isFocused) {
          onChange(newValue);
        }
      }
    };

    return (
      <Input
        ref={ref}
        label={label}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        error={error}
        helperText={helperText}
        required={required}
        disabled={disabled}
        className={cn('text-right', className)}
        placeholder="$0"
        {...props}
      />
    );
  }
);

CurrencyInput.displayName = 'MiraCurrencyInput';

export default CurrencyInput;
